import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { FileOpener } from '@capawesome-team/capacitor-file-opener';

import { user as userApi } from '../services/api';
import AttachmentCacheService from '../services/attachmentCacheService';
import {
    Bell, AlertTriangle, AlertCircle, Info, FileText,
    X, ChevronRight, Loader2, ExternalLink
} from 'lucide-react';
import { useToast } from '../context/ToastContext';

// API base for native platforms
const API_BASE = Capacitor.isNativePlatform() ? 'https://calendario.xerp.es' : '';


const PRIORIDADES = {
    normal: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10', ring: 'ring-blue-500/20' },
    importante: { icon: AlertCircle, color: 'text-yellow-400', bg: 'bg-yellow-500/10', ring: 'ring-yellow-500/20' },
    urgente: { icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10', ring: 'ring-red-500/20' },
};

// Componente para lista de avisos (reutilizable)
const AvisosList = ({ avisos, onSelectAviso }) => {
    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-3">
            {avisos.map(aviso => {
                const prioridadInfo = PRIORIDADES[aviso.prioridad] || PRIORIDADES.normal;
                const PrioridadIcon = prioridadInfo.icon;

                return (
                    <button
                        key={aviso.id}
                        onClick={() => onSelectAviso(aviso)}
                        className={`w-full text-left p-4 rounded-xl border transition-all ${aviso.leido
                            ? 'bg-white/5 border-white/10 hover:border-white/20'
                            : `${prioridadInfo.bg} border-2 ${prioridadInfo.ring} ring-2`
                            }`}
                    >
                        <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg ${prioridadInfo.bg} flex items-center justify-center flex-shrink-0`}>
                                <PrioridadIcon className={`w-4 h-4 ${prioridadInfo.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                    <h3 className="font-medium" style={{ color: aviso.leido ? 'var(--text-secondary)' : 'var(--text-primary)' }}>
                                        {aviso.titulo}
                                    </h3>
                                    {!aviso.leido && (
                                        <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full flex-shrink-0">
                                            Nuevo
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm line-clamp-1 mt-1" style={{ color: 'var(--text-muted)' }}>
                                    {aviso.contenido}
                                </p>
                                <div className="flex items-center gap-3 mt-2">
                                    <span className="text-xs text-gray-500">
                                        {formatDate(aviso.fechaPublicacion)}
                                    </span>
                                    {aviso.numAdjuntos > 0 && (
                                        <span className="flex items-center gap-1 text-xs text-gray-500">
                                            <FileText className="w-3 h-3" />
                                            {aviso.numAdjuntos}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

// Modal de detalle de aviso
const AvisoDetailModal = ({ aviso, onClose, loading }) => {
    const { error: toastError } = useToast();
    const contentRef = useRef(null);

    // Reset scroll to top when modal opens
    useEffect(() => {
        if (contentRef.current && !loading) {
            contentRef.current.scrollTop = 0;
        }
    }, [aviso, loading]);


    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const [openingAttachment, setOpeningAttachment] = useState(null);

    const handleOpenAttachment = async (adj) => {
        try {
            setOpeningAttachment(adj.id);

            const token = localStorage.getItem('token');
            const isPDF = adj.tipoMime?.includes('pdf') || adj.nombre?.toLowerCase().endsWith('.pdf');
            const mimeType = adj.tipoMime || (isPDF ? 'application/pdf' : 'application/octet-stream');

            if (Capacitor.isNativePlatform()) {
                // NATIVE PLATFORM: Download to app storage, open with system viewer

                // 1. Check if already cached
                let fileUri = null;
                const cachedPath = await AttachmentCacheService.getCachedPath(adj.id);

                if (cachedPath) {
                    fileUri = await AttachmentCacheService.getFileUri(cachedPath);
                } else {
                    // Download and cache
                    const response = await fetch(`${API_BASE}/api/avisos/adjuntos/${adj.id}`, {
                        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                        credentials: 'include'
                    });

                    if (!response.ok) {
                        throw new Error('Error al descargar el archivo');
                    }

                    const blob = await response.blob();

                    // Cache to app internal storage
                    const localPath = await AttachmentCacheService.cacheAttachment(
                        adj.id,
                        aviso.id,
                        blob,
                        adj.nombre || 'attachment.pdf',
                        mimeType
                    );

                    if (localPath) {
                        fileUri = await AttachmentCacheService.getFileUri(localPath);
                    }
                }

                // 3. Open with system file viewer
                if (fileUri) {
                    await FileOpener.openFile({
                        path: fileUri,
                        mimeType: mimeType
                    });
                } else {
                    throw new Error('No se pudo guardar el archivo');
                }

            } else {
                // WEB PLATFORM: Use blob URLs (works in browser)
                const response = await fetch(`${API_BASE}/api/avisos/adjuntos/${adj.id}`, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                    credentials: 'include'
                });

                if (!response.ok) {
                    throw new Error('Error al descargar el archivo');
                }

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);

                if (isPDF) {
                    window.open(blobUrl, '_blank');
                } else {
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = adj.nombre || 'archivo';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }

                setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
            }

        } catch (error) {
            console.error('Error opening attachment:', error);
            toastError(`Error al abrir el archivo: ${error.message || 'Error desconocido'}`);
        } finally {
            setOpeningAttachment(null);
        }
    };


    return createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-[9999] p-4 pt-16 overflow-y-auto">
            <div className="bg-[var(--bg-secondary)] border border-[var(--glass-border)] rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col my-auto shadow-2xl">


                {loading ? (
                    <div className="p-8 flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="p-5 border-b border-[var(--glass-border)] flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-lg ${PRIORIDADES[aviso.prioridad]?.bg || 'bg-blue-500/10'} flex items-center justify-center flex-shrink-0`}>
                                {(() => {
                                    const Icon = PRIORIDADES[aviso.prioridad]?.icon || Info;
                                    return <Icon className={`w-5 h-5 ${PRIORIDADES[aviso.prioridad]?.color || 'text-blue-400'}`} />;
                                })()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2 className="text-lg font-semibold text-[var(--text-primary)]">{aviso.titulo}</h2>
                                <p className="text-sm text-[var(--text-muted)] mt-1">
                                    {formatDate(aviso.fechaPublicacion)}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--glass-bg)] rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Content */}
                        <div ref={contentRef} className="p-5 overflow-y-auto flex-1 min-h-0 bg-[var(--bg-primary)]">
                            <div className="text-[var(--text-secondary)] whitespace-pre-wrap break-words leading-relaxed">
                                {(() => {
                                    if (!aviso.contenido) return null;

                                    // Expresión regular para detectar URLs
                                    const urlRegex = /(https?:\/\/[^\s]+)/g;
                                    const parts = aviso.contenido.split(urlRegex);

                                    return parts.map((part, i) => {
                                        if (part.match(urlRegex)) {
                                            const handleClick = async (e) => {
                                                if (Capacitor.isNativePlatform()) {
                                                    e.preventDefault();
                                                    await Browser.open({ url: part });
                                                }
                                            };

                                            return (
                                                <a
                                                    key={i}
                                                    href={part}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={handleClick}
                                                    className="text-cyan-400 hover:text-cyan-300 underline underline-offset-4 decoration-cyan-500/30 hover:decoration-cyan-400 font-medium break-all"
                                                >
                                                    {part}
                                                </a>
                                            );
                                        }
                                        return part;
                                    });
                                })()}
                            </div>


                            {/* Adjuntos */}
                            {aviso.adjuntos && aviso.adjuntos.length > 0 && (
                                <div className="mt-6">
                                    <h3 className="text-sm font-medium text-gray-400 mb-3">Archivos adjuntos</h3>
                                    <div className="space-y-2">
                                        {aviso.adjuntos.map(adj => (
                                            <button
                                                key={adj.id}
                                                onClick={() => handleOpenAttachment(adj)}
                                                disabled={openingAttachment === adj.id}
                                                className="flex items-center gap-3 p-3 bg-[var(--glass-bg)] rounded-lg hover:border-[var(--glass-border)] border border-transparent transition-colors active:bg-cyan-500/20 w-full text-left disabled:opacity-50"
                                            >
                                                <FileText className="w-5 h-5 text-cyan-400" />
                                                <span className="flex-1 text-[var(--text-secondary)] truncate">{adj.nombre}</span>
                                                {openingAttachment === adj.id ? (
                                                    <>
                                                        <span className="text-xs text-cyan-400 mr-2">Abriendo...</span>
                                                        <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="text-xs text-gray-500 mr-2">Abrir</span>
                                                        <ExternalLink className="w-4 h-4 text-cyan-400" />
                                                    </>
                                                )}
                                            </button>
                                        ))}


                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-[var(--glass-border)] bg-[var(--bg-secondary)]">
                            <button
                                onClick={onClose}
                                className="w-full py-3 bg-cyan-500 hover:bg-cyan-400 text-white font-bold rounded-xl transition-colors shadow-lg shadow-cyan-500/20"
                            >
                                Cerrar
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body
    );

};

const UserAvisos = ({ variant = 'default' }) => {
    const [avisos, setAvisos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAviso, setSelectedAviso] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [hasNewNotification, setHasNewNotification] = useState(false);

    useEffect(() => {
        fetchAvisos();
    }, []);

    // Listen for push notifications to refresh avisos
    useEffect(() => {
        const handlePushNotification = (event) => {
            // Refresh avisos list
            fetchAvisos();
            // Show visual feedback
            setHasNewNotification(true);
            setTimeout(() => setHasNewNotification(false), 3000);
        };

        window.addEventListener('pushNotificationReceived', handlePushNotification);
        return () => {
            window.removeEventListener('pushNotificationReceived', handlePushNotification);
        };
    }, []);

    const fetchAvisos = async () => {
        try {
            setLoading(true);
            const data = await userApi.getAvisos();
            setAvisos(data);
        } catch (err) {
            console.error('Error fetching avisos:', err);
        } finally {
            setLoading(false);
        }
    };


    const openAviso = async (aviso) => {
        setLoadingDetail(true);
        try {
            const data = await userApi.getAviso(aviso.id);
            setSelectedAviso(data);
            setAvisos(prev => prev.map(a => a.id === aviso.id ? { ...a, leido: true } : a));
        } catch (err) {
            console.error('Error fetching aviso detail:', err);
        } finally {
            setLoadingDetail(false);
        }
    };

    const closeDetail = () => {
        setSelectedAviso(null);
    };

    const unreadCount = avisos.filter(a => !a.leido).length;

    // Variant: badge (small icon that opens a modal with list)
    if (variant === 'badge') {
        return (
            <>
                {/* Badge Button */}
                <button
                    onClick={() => setIsModalOpen(true)}
                    className={`relative p-2 rounded-lg hover:bg-white/10 transition-colors ${hasNewNotification ? 'animate-bounce' : ''}`}
                    title="Avisos"
                >
                    <Bell className={`w-5 h-5 ${hasNewNotification ? 'text-cyan-400' : 'text-gray-400'}`} />
                    {unreadCount > 0 && (
                        <span className={`absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center ${hasNewNotification ? 'animate-ping' : 'animate-pulse'}`}>
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </button>


                {/* Modal with list */}
                {isModalOpen && createPortal(
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-start justify-center pt-20 p-4">
                        <div className="bg-[var(--bg-secondary)] border border-[var(--glass-border)] rounded-2xl shadow-2xl w-full max-w-lg max-h-[70vh] flex flex-col overflow-hidden">
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b border-[var(--glass-border)]">
                                <div className="flex items-center gap-2">
                                    <Bell className="w-5 h-5 text-cyan-400" />
                                    <h2 className="text-lg font-semibold text-[var(--text-primary)]">Avisos</h2>
                                    {unreadCount > 0 && (
                                        <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                                            {unreadCount} nuevos
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--glass-bg)] rounded-xl transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-4">
                                {loading ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
                                    </div>
                                ) : avisos.length === 0 ? (
                                    <div className="text-center py-8">
                                        <Bell className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                                        <p className="text-gray-400">No hay avisos pendientes</p>
                                    </div>
                                ) : (
                                    <AvisosList avisos={avisos} onSelectAviso={openAviso} />
                                )}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}


                {/* Detail Modal */}
                {selectedAviso && (
                    <AvisoDetailModal
                        aviso={selectedAviso}
                        onClose={closeDetail}
                        loading={loadingDetail}
                    />
                )}
            </>
        );
    }

    // Default variant (inline list for desktop)
    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
            </div>
        );
    }

    if (avisos.length === 0) {
        return (
            <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl p-6 text-center">
                <Bell className="w-10 h-10 text-[var(--text-muted)] opacity-50 mx-auto mb-3" />
                <p className="text-[var(--text-muted)]">No hay avisos pendientes</p>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-3">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Bell className="w-5 h-5 text-cyan-400" />
                        <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Avisos</h2>
                        {unreadCount > 0 && (
                            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                                {unreadCount}
                            </span>
                        )}
                    </div>
                </div>
                <AvisosList avisos={avisos} onSelectAviso={openAviso} />
            </div>

            {selectedAviso && (
                <AvisoDetailModal
                    aviso={selectedAviso}
                    onClose={closeDetail}
                    loading={loadingDetail}
                />
            )}
        </>
    );
};

export default UserAvisos;
